import { useCallback, useEffect, useRef, useState } from "react";
import { Icon } from "./Icon";
import { useTranslation } from "react-i18next";
import { useApp } from "../context";
import { PHOTO_KINDS, type Photo, type PhotoKind } from "../data";
import { shrinkPhoto } from "../photo";
import { ErrorText } from "./ErrorText";
import { Help } from "./Help";

/**
 * Photos of a work order (or of a vehicle): the big camera button opens the
 * phone camera directly, the second button picks from the gallery. Images are
 * shrunk before upload.
 */
export function Photos({ vehicleId, workOrderId, readOnly, defaultKind = "vehicle", onChange }: {
  vehicleId: string | null;
  workOrderId?: string;
  readOnly?: boolean;
  defaultKind?: PhotoKind;
  /** The photos after loading and after every change (the work order counts the damage photos for its intake receipt). */
  onChange?: (photos: Photo[]) => void;
}) {
  const { t } = useTranslation();
  const { store, workshop } = useApp();
  const [photos, setPhotos] = useState<Photo[] | null>(null);
  const [kind, setKind] = useState<PhotoKind>(defaultKind);
  const [busy, setBusy] = useState(0);
  const [error, setError] = useState<unknown>(null);
  const [open, setOpen] = useState<Photo | null>(null);
  const camera = useRef<HTMLInputElement>(null);
  const gallery = useRef<HTMLInputElement>(null);
  const date = (iso: string) => new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(iso));

  const load = useCallback(async () => {
    try {
      setPhotos(await store.listPhotos(workOrderId ? { workOrderId } : { vehicleId: vehicleId ?? undefined }));
    } catch (e) {
      setError(e);
      setPhotos([]);
    }
  }, [store, workOrderId, vehicleId]);
  useEffect(() => void load(), [load]);
  useEffect(() => {
    if (photos) onChange?.(photos);
  }, [photos, onChange]);

  const add = async (files: FileList | null) => {
    const list = Array.from(files ?? []);
    if (list.length === 0) return;
    setError(null);
    setBusy(list.length);
    for (const f of list) {
      try {
        const image = await shrinkPhoto(f);
        const p = await store.addPhoto(workshop.id, { vehicleId, workOrderId: workOrderId ?? null, kind }, image);
        setPhotos((cur) => [p, ...(cur ?? [])]);
      } catch (e) {
        setError(e);
      }
      setBusy((n) => n - 1);
    }
  };

  const remove = async (p: Photo) => {
    if (!globalThis.confirm?.(t("photos.confirmDelete"))) return;
    try {
      await store.deletePhoto(p.id);
      setOpen(null);
      setPhotos((cur) => (cur ?? []).filter((x) => x.id !== p.id));
    } catch (e) {
      setError(e);
    }
  };

  return (
    <section className="photos">
      <h2>
        {t("photos.title")} <Help id="photos.section" />
      </h2>
      {!readOnly && (
        <div className="photo-add no-print">
          <div className="kind-pick" role="group" aria-label={t("photos.kind")}>
            {PHOTO_KINDS.map((k) => (
              <button key={k} type="button" aria-pressed={kind === k} onClick={() => setKind(k)}>
                {t(`photos.kind.${k}`)}
              </button>
            ))}
          </div>
          <div className="photo-buttons">
            <button type="button" className="primary camera" onClick={() => camera.current?.click()} disabled={busy > 0}>
              <Icon name="photo" size={28} />
              {t("photos.take")}
            </button>
            <button type="button" onClick={() => gallery.current?.click()} disabled={busy > 0}>
              {t("photos.pick")}
            </button>
          </div>
          <input ref={camera} type="file" accept="image/*" capture="environment" hidden aria-label={t("photos.take")} onChange={(e) => { void add(e.target.files); e.target.value = ""; }} />
          <input ref={gallery} type="file" accept="image/*" multiple hidden aria-label={t("photos.pick")} onChange={(e) => { void add(e.target.files); e.target.value = ""; }} />
          {busy > 0 && <p className="info" role="status">{t("photos.uploading", { count: String(busy) })}</p>}
        </div>
      )}
      <ErrorText error={error} />
      {photos === null ? (
        <p>{t("common.loading")}</p>
      ) : photos.length === 0 ? (
        <p className="muted">{t("photos.none")}</p>
      ) : (
        <ul className="photo-grid">
          {photos.map((p) => (
            <li key={p.id}>
              <button type="button" className="thumb" onClick={() => setOpen(p)} aria-label={`${t(`photos.kind.${p.kind}`)} ${date(p.createdAt)}`}>
                <img src={p.url} alt="" loading="lazy" />
                <span className="thumb-kind">{t(`photos.kind.${p.kind}`)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {open && (
        <div className="viewer" role="dialog" aria-modal="true" aria-label={t("photos.title")} onClick={() => setOpen(null)}>
          <div className="viewer-box" onClick={(e) => e.stopPropagation()}>
            <img src={open.url} alt={t(`photos.kind.${open.kind}`)} />
            <div className="viewer-bar">
              <span>
                <strong>{t(`photos.kind.${open.kind}`)}</strong> · {date(open.createdAt)}
              </span>
              <span className="viewer-actions">
                {!readOnly && (
                  <button type="button" className="danger" onClick={() => void remove(open)}>
                    {t("common.delete")}
                  </button>
                )}
                <button type="button" className="primary" onClick={() => setOpen(null)}>
                  {t("common.close")}
                </button>
              </span>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
